import { useAtom } from "jotai";
import { BasicInput } from "../../../../../components/BasicInput";
import { currentStep } from "../../../../../../app/store";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import BasicOptions from "../../../../../components/BasicOptions";
import { BasicSelect } from "@/view/components/BasicSelect";
import { useQuery } from "@tanstack/react-query";
import { transactionsService } from "@/app/services/transactionService";
import { Button } from "@/view/components/ui/button";
import { useC6Bank } from "@/app/contexts/C6BankContext";
import { customerService } from "@/app/services/customerService";
import { useToast } from "@/view/components/ui/use-toast";
import { useState } from "react";

export const bankFormSchema = z.object({
  accountType: z
    .string({ required_error: "* obrigatório" })
    .min(1, "* obrigatório"),
  bank: z.string({ required_error: "* obrigatório" }).min(1, "* obrigatório"),
  agency: z.string().min(3, "* obrigatório").max(5, "No maximo 5 caracteres"),
  account: z.string().min(4, "* obrigatório"),
});

type bankFormData = z.infer<typeof bankFormSchema>;
interface BankInfoFormProps {
  visible?: boolean;
}
export default function BankInfoForm({ visible }: BankInfoFormProps) {
  const [, setStep] = useAtom(currentStep);

  const { toast } = useToast();

  const { customerData, setCustomerData, setProposalData } = useC6Bank();

  const [isLoading, setIsLoading] = useState(false);

  const { data: banks, isLoading: isLoadingBanks } = useQuery({
    queryKey: ["banks"],
    queryFn: () => transactionsService.banks(),
    select(data) {
      return data.banks.map(({ label, value }) => ({
        label: `${value} - ${label}`,
        value,
      }));
    },
  });

  const bankForm = useForm<bankFormData>({
    resolver: zodResolver(bankFormSchema),
  });

  const {
    handleSubmit,
    formState: { errors },
  } = bankForm;

  const submitForm = async (data: bankFormData) => {
    try {
      setIsLoading(true);

      const bankAccount = {
        bank: data.bank,
        agency: data.agency,
        account: data.account,
        accountType: data.accountType,
      };

      setCustomerData({
        bankAccount,
      });

      const payload = {
        ...customerData,
        bankAccount,
      };

      const response = await customerService.createCustomer(payload as any);

      setProposalData({
        customerId: response._id,
      });

      setStep((state) => state + 1);
    } catch (err: any) {
      toast({
        title: "Erro ao criar cliente",
        description:
          err?.message || err?.data?.message || err?.response?.data?.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={visible ? "flex flex-col gap-8 max-sm:gap-4" : "hidden"}>
      <div className="grid grid-cols-inputs gap-y-4 max-sm:grid-cols-1 max-sm:gap-y-3">
        <FormProvider {...bankForm}>
          <BasicOptions
            options={[
              { label: "Poupança", value: "CP" },
              { label: "Corrente", value: "CC" },
            ]}
            name="accountType"
            label="Tipo de conta"
            error={errors.accountType?.message}
          />

          <div className="max-sm:hidden" />

          {!isLoadingBanks && (
            <div className="mr-4 max-sm:mr-0">
              <BasicSelect
                name="bank"
                label="Banco"
                error={errors.bank?.message}
                options={banks || []}
                placeholder="Selecione seu banco"
                tabIndex={6}
              />
            </div>
          )}

          <div className="mr-8 grid grid-cols-2 gap-4 max-sm:mr-0 max-sm:grid-cols-1 max-sm:gap-3">
            <BasicInput
              tabIndex={0}
              name="agency"
              label="Agência"
              error={errors.agency?.message}
              type="text"
              inputMode="numeric"
              maxLength={4}
            />
            <BasicInput
              tabIndex={0}
              name="account"
              label="Conta"
              error={errors.account?.message}
              type="text"
              inputMode="numeric"
              maxLength={11}
            />
          </div>
        </FormProvider>
      </div>
      <div className="flex justify-end gap-5 max-sm:flex-col max-sm:gap-3">
        <Button
          type="button"
          className="rounded-full bg-white02 px-8 py-3 font-semibold text-primary hover:bg-white02 max-sm:order-2"
          onClick={() => setStep((state) => state - 1)}
        >
          Voltar
        </Button>
        <Button
          className="rounded-full px-8 py-3 font-semibold text-white max-sm:order-1"
          isLoading={isLoading}
          disabled={isLoading}
          onClick={handleSubmit(submitForm)}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
